import {EventEmitter, Inject, Injectable} from '@angular/core';
import {ResponseDto} from '../dto/ResponseDto';
import {BotatoChatStorageService} from './botato-chat-storage.service';
import {CommunicationService} from './communication.service';
import {BotatoChatLocalHandlerService} from './botato-chat-local-handler.service';
import {DialogConfigProviderService} from './dialog-config-provider.service';
import {BotatoLocalConfig} from '../botato-local.config';
import {BotatoChatLocalContextProviderService} from './botato-chat-local-context-provider.service';
import {TranslateService} from '@ngx-translate/core';
import {LocalMessageSender} from '../LocalMessageSender';
import {LocalRecipient} from '../LocalRecipient';
import {Observable} from 'rxjs/internal/Observable';
import {
    KeywordRecognizerService
} from 'botato-botengine-browser/botato/botato-botengine/source/NlpService/KeywordRecognizer/KeywordRecognizerService.js';
import {Keyword} from 'botato-botengine-browser/botato/botato-botengine/source/NlpService/KeywordRecognizer/Keyword.js';
import {SenderIdentity} from 'botato-botengine-browser/botato/botato-botengine/source/Core/SenderIdentity.js';
import {NlpInterpretation} from 'botato-botengine-browser/botato/botato-botengine/source/Core/Nlp/NlpInterpretation.js';
import {
    InputSourceType
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/dto/InputSourceType.js';
import {
    EventInterpretation
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/Nlp/EventInterpretation.js';
import {SentMessage} from 'botato-botengine-browser/botato/botato-botengine/source/Core/Session/SentMessage.js';
import {
    WebSocketSpecificData
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/WebSocketSpecificData.js';
import {
    MessageRouter
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/MessageRouter.js';
import {BotatoLocale} from 'botato-botengine-browser/botato/botato-botengine/source/Core/Localization/BotatoLocale.js';

@Injectable()
export class BotatoChatLocalCommunicationService extends CommunicationService {

    // Never emits locally: there is no second client that could take over the connection
    public alreadyConnectChanged$: Observable<boolean> = new EventEmitter<boolean>();
    public authenticationFailed: EventEmitter<void> = new EventEmitter<void>();
    public messageReceived: EventEmitter<ResponseDto> = new EventEmitter<ResponseDto>();
    public messageSent: EventEmitter<void> = new EventEmitter<void>();
    public eventSent: EventEmitter<void> = new EventEmitter<void>();
    public historyReceived: EventEmitter<(ResponseDto | string)[]> = new EventEmitter<(ResponseDto | string)[]>();
    public connectionStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    public startTyping: EventEmitter<ResponseDto> = new EventEmitter<ResponseDto>();
    public messageSendingStarted: EventEmitter<void> = new EventEmitter<void>();
    public messageSendingFinished: EventEmitter<void> = new EventEmitter<void>();
    public thinkingIndicator: EventEmitter<void> = new EventEmitter<void>();
    public unreadStatusReceived: EventEmitter<boolean> = new EventEmitter<boolean>();
    public lastReadAtReceived: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();

    private userId: string | undefined;
    public alreadyConnected: boolean = false;

    constructor(private storageService: BotatoChatStorageService,
                private configProvider: DialogConfigProviderService,
                @Inject('BOTATO_CONFIG') private botatoConfig: BotatoLocalConfig,
                private botatoChatLocalHandlerService: BotatoChatLocalHandlerService,
                private botatoChatLocalContextProvider: BotatoChatLocalContextProviderService,
                private translateService: TranslateService) {
        super();

        this.messageReceived.subscribe((botResult: ResponseDto) => {
            this.lastBotResult = botResult;
        });
    }

    public async sendMessage(text: string): Promise<void> {
        const keywords: Keyword[] = KeywordRecognizerService.loadKeywords(this.configProvider.getDialogConfig());
        // Only the application user id is known locally; the botengine declares the other identifiers as
        // required although the keyword recognition never reads them.
        const nlpSessionId = new SenderIdentity(...([undefined, undefined, undefined, this.userId, undefined, undefined, undefined, undefined] as unknown as ConstructorParameters<typeof SenderIdentity>));
        const interpretation: NlpInterpretation = await new KeywordRecognizerService(keywords, this.botatoConfig.defaultFallbackIntent).interpret(text, this.toBotatoLocale(this.translateService.getCurrentLang()), nlpSessionId, this.botatoChatLocalContextProvider.get());
        await this.sendInterpretAndReply(interpretation);
        this.messageSent.emit();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async sendEvent(eventName: string, inputSource: InputSourceType, eventPayload: any): Promise<void> {
        const interpretation = new EventInterpretation(eventName, this.toBotatoLocale(this.translateService.getCurrentLang()), eventPayload, inputSource);
        await this.sendInterpretAndReply(interpretation);
        this.eventSent.emit();
    }

    public setBotId(botId: string): void {
        this.botId = botId;
    }

    public async welcome(): Promise<void> {

        await new Promise<void>(resolve => {
            // TODO Reihenfolge Problem -> Wenn hier nicht gewartet wird, senden wir den messageReceived bevor jemand darauf hört
            setTimeout(async () => {
                await this.sendInterpretAndReply({
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    getIntentName: () => 'start', getAllEntities: () => {
                    }
                } as NlpInterpretation);

                resolve();
            });
        });

        this.eventSent.emit();
    }

    private toBotatoLocale(lang: string): BotatoLocale {
        const [language, region = ''] = lang.split('-');
        return new BotatoLocale(language, region);
    }

    private async sendInterpretAndReply(interpretation: NlpInterpretation): Promise<SentMessage> {

        const botResult = await this.botatoChatLocalHandlerService.handle(interpretation);

        const localRecipient = new LocalRecipient(this.messageReceived, this.startTyping);

        if (this.botatoConfig.typingTimeoutCalculator === undefined) {
            throw new Error('It is mandatory to define a typingTimeoutCalculator in BotatoConfig (see app.module.ts)');
        }

        const router: MessageRouter<LocalRecipient, WebSocketSpecificData> = new MessageRouter<LocalRecipient, WebSocketSpecificData>(new LocalMessageSender(this.botatoConfig.typingTimeoutCalculator));
        return router.send(botResult.responseItems, botResult.quickReplies, localRecipient,
            new WebSocketSpecificData(botResult.botResultOptions, botResult.customBotData));
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async connect(): Promise<boolean> {
        throw new Error('Called connect on local caht - there is nothing to connect here!');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setAccessTokenProvider(provider: () => Promise<string>): void {
        throw new Error('Not supported on local');
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async disconnect(): Promise<void> {
        throw new Error('Method not implemented.');
    }

    public async sendMarkAsRead(): Promise<void> {
        // No-op for local communication
    }
}
